import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createCmsMessage,
  CreateMessagePayload,
  fetchCmsMessageDetails,
  fetchCmsMessages,
  MessageDetailItem,
  MessageItem,
  MessageListData,
} from "../../apis/cmsMessageApi";

// ----------------------
// TYPES
// ----------------------

interface CmsMessageState {
  list: MessageItem[]; // admin messages
  studyTeam: MessageItem[]; // study team messages

  messageDetails: MessageDetailItem[];

  totalCount: number;
  adminCount: number;
  studyTeamCount: number;
  messageDetailsCount: number;

  loading: boolean;
  createLoading: boolean;
  detailsLoading: boolean;
  error: string | null;
}

const initialState: CmsMessageState = {
  list: [],
  studyTeam: [],

  messageDetails: [],

  totalCount: 0,
  adminCount: 0,
  studyTeamCount: 0,

  messageDetailsCount: 0,

  loading: false,
  createLoading: false,
  detailsLoading: false,

  error: null,
};

// ----------------------
// ASYNC THUNKS
// ----------------------

export const getCmsMessages = createAsyncThunk(
  "cmsMessages/getCmsMessages",
  async (
    {
      role,
      pageId = 1,
      limit = 30,
    }: { role: "admin" | "study team"; pageId?: number; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchCmsMessages(role, pageId, limit);
      return { role, data: response.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.data || "Failed to fetch messages",
      );
    }
  },
);

export const sendCmsMessage = createAsyncThunk(
  "cmsMessages/sendCmsMessage",
  async (payload: CreateMessagePayload, { rejectWithValue }) => {
    try {
      const response = await createCmsMessage(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.data || "Failed to send message",
      );
    }
  },
);

export const getCmsMessageDetails = createAsyncThunk(
  "cmsMessages/getCmsMessageDetails",
  async (
    {
      userId,
      pageId = 1,
      limit = 10,
    }: {
      userId: string;
      pageId?: number;
      limit?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchCmsMessageDetails(userId, pageId, limit);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.data || "Failed to fetch message details",
      );
    }
  },
);

// ----------------------
// SLICE
// ----------------------

const cmsMessageSlice = createSlice({
  name: "cmsMessages",
  initialState,
  reducers: {
    clearCmsMessages: (state) => {
      state.list = [];
      state.studyTeam = [];

      state.messageDetails = [];

      state.totalCount = 0;
      state.adminCount = 0;
      state.studyTeamCount = 0;

      state.messageDetailsCount = 0;

      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ================================
         FETCH CMS MESSAGES
      ================================= */

      .addCase(getCmsMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getCmsMessages.fulfilled, (state, action) => {
        state.loading = false;

        const data: MessageListData = action.payload.data;

        const rows = data?.rows || [];

        // Convert counts safely (API sends strings)
        const adminCount = Number(data?.adminCount || 0);
        const studyTeamCount = Number(data?.studyTeamCount || 0);
        const totalCount = Number(data?.count || 0);

        // Store counts globally
        state.adminCount = adminCount;
        state.studyTeamCount = studyTeamCount;
        state.totalCount = totalCount;

        if (action.payload.role === "admin") {
          state.list = rows;
        } else if (action.payload.role === "study team") {
          state.studyTeam = rows;
        }
      })

      .addCase(getCmsMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to fetch messages");
      })

      /* ================================
         SEND MESSAGE
      ================================= */

      .addCase(sendCmsMessage.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })

      .addCase(sendCmsMessage.fulfilled, (state, action) => {
        state.createLoading = false;

        const apiMessage = action.payload?.data || "Message sent successfully.";

        toast.success(apiMessage);
      })

      .addCase(sendCmsMessage.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to send message");
      })

      .addCase(getCmsMessageDetails.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })

      .addCase(getCmsMessageDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;

        state.messageDetails = action.payload?.rows || [];
        state.messageDetailsCount = Number(action.payload?.count || 0);
      })

      .addCase(getCmsMessageDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload as string;

        toast.error(state.error || "Failed to fetch message details");
      });
  },
});

export const { clearCmsMessages } = cmsMessageSlice.actions;

export default cmsMessageSlice.reducer;
